import { forwardRef, useImperativeHandle, useRef } from 'react';

import { CollisionFilterExtension } from '@deck.gl/extensions';
import { TextLayer } from '@deck.gl/layers';
import { MapboxLayer } from '@deck.gl/mapbox';
import { Checkbox, Flex, Text } from '@raystack/apsara';

import { VirtualizedFor } from '@src/hoc';

import styles from './styles.module.css';

export const FarmList = forwardRef(
  (
    {
      data,
      showLabels,
      setShowLabels,
      activeFarmID,
      handleActiveFarm,
      handleHoveredID,
      handleMouseLeave
    },
    ref
  ) => {
    const listRef = useRef(null);

    // hook to scroll to element
    useImperativeHandle(ref, () => {
      return {
        scrollToItem: index => {
          listRef.current?.scrollToItem(index, 'center');
        }
      };
    }, []);

    return (
      <Flex direction='column' style={{ width: '100%', flex: 1 }}>
        <div className={styles.listHeader}>
          <Text size={3} weight={500} variant='primary'>
            Farms detected: {data.length}
          </Text>

          <Flex align='center' gap='small'>
            <Text size={3} weight={500} variant='secondary'>
              Labels
            </Text>

            <Checkbox checked={showLabels} onCheckedChange={setShowLabels} />
          </Flex>
        </div>

        <VirtualizedFor
          ref={listRef}
          key={data.length}
          data={data}
          itemHeight={50}
          totalCount={data.length}
        >
          {({ index, data, style }) => (
            <div
              id={data[index].properties.Farm_ID}
              className={styles.farmContainer}
              onClick={() =>
                handleActiveFarm(data[index].properties.Farm_ID, data[index])
              }
              data-selected={data[index].properties.Farm_ID === activeFarmID}
              key={index}
              onMouseEnter={() =>
                handleHoveredID(data[index].properties.Farm_ID)
              }
              onMouseLeave={handleMouseLeave}
              data-test-id={`farm-field-cell-${index}`}
              style={style}
            >
              <Flex align='center' gap='small'>
                <div className={styles.farmId}>
                  <Text size={1} weight={500} variant='secondary'>
                    {index + 1}
                  </Text>
                </div>
                <Text>Farm {data[index].properties.Farm_ID}</Text>
              </Flex>

              <Text>
                {data[index].properties.Area.toFixed(2)} m<sup>2</sup>
              </Text>
            </div>
          )}
        </VirtualizedFor>
      </Flex>
    );
  }
);

FarmList.displayName = 'FarmList';

export function createTextLayer(data, { id = 'text-layer', fontSize = 2 }) {
  const layer = new MapboxLayer({
    id,
    type: TextLayer,
    data,
    visible: true,
    characterSet: 'auto',
    fontFamily: 'Inter',
    fontSettings: { buffer: 8, sdf: true },
    getText: d => d.text,
    getPosition: d => d.coordinates,
    getSize: 5,
    sizeUnits: 'pixels',
    getColor: [255, 255, 255, 255],
    sizeScale: fontSize,
    pickable: true,
    maxWidth: 64 * 12,
    outlineWidth: 4,
    collisionEnabled: true,
    extensions: [new CollisionFilterExtension()]
  });

  return layer;
}
